import type { AlertNoteVariant } from '../../alerts/index.js';
import { formatDay } from './format.js';

type TFn = (key: string, options?: Record<string, unknown>) => string;

/** The sentence an inventory note variant reads as — one wording for the
 *  web's dashboard Delivery card and the mobile stock rows. */
export function logisticsNoteLine(
  variant: AlertNoteVariant,
  facts: { value: number; date?: string; ref?: string },
  t: TFn,
  lang: string,
): string {
  const N = 'logistics.inventory';
  const day = facts.date ? formatDay(facts.date, lang) : '';
  const ranOut = t(`${N}.ranOut`);
  switch (variant) {
    case 'gap':
      return t(`${N}.overdueHint`, { gap: facts.value });
    case 'gap-today':
      return t(`${N}.overdueHintToday`);
    case 'gap-draft':
      return t(`${N}.overdueDraft`, { ref: facts.ref });
    // The ran-out row builds its line from two keys — the "estimate ran out"
    // stem and whatever is (or is not) on the way behind it.
    case 'ran-out':
      return `${ranOut} ${t(`${N}.noDelivery`)}`;
    case 'ran-out-arrived':
      return `${ranOut} ${t(`${N}.arrivedOn`, { d: day })}`;
    case 'ran-out-due':
      return `${ranOut} ${t(`${N}.dueAsk`)}`;
    // Ran out with the replacement still on its way — the row's in-flight
    // words ("arriving" on an agreed date, "estimated" on a projected one).
    case 'ran-out-incoming':
      return `${ranOut} ${facts.date ? t(`${N}.deliveryIncomingOn`, { date: day }) : t(`${N}.deliveryIncoming`)}`;
    case 'ran-out-incoming-about':
      return `${ranOut} ${t(`${N}.deliveryIncomingAbout`, { date: day })}`;
    case 'late-in-flight':
      return t(`${N}.deliveryIncomingLateOn`, {
        n: facts.value,
        date: day,
      });
    case 'late-arrived':
      return t(`${N}.arrivedOverdueOn`, {
        n: facts.value,
        date: day,
      });
    case 'untracked-due':
      return t(`${N}.untracked.dueReached`);
  }
}
